import { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Sparkles, Music, ArrowLeft, Flame, Moon, Zap, Cloud, Rocket, Droplets, Heart, ListMusic, Plus, Loader2, X, Globe, Lock } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import BeatPlayer from "../components/BeatPlayer";
import RecommendedBeats from "../components/RecommendedBeats";
import ShareButton from "../components/ShareButton";
import { useAuth } from "../App";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MOOD_ICONS = {
  Dark: Moon,
  Aggressive: Flame,
  Bouncy: Zap,
  Melodic: Music,
  Chill: Cloud,
  Hype: Rocket,
  Sad: Droplets,
  Ethereal: Sparkles,
};

const DiscoverPage = () => {
  const [moods, setMoods] = useState([]);
  const [curated, setCurated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [playlists, setPlaylists] = useState([]);
  const [showCreatePlaylist, setShowCreatePlaylist] = useState(false);
  const [newPlaylistName, setNewPlaylistName] = useState("");
  const [newPlaylistDesc, setNewPlaylistDesc] = useState("");
  const [creating, setCreating] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [moodsRes, curatedRes] = await Promise.all([
          axios.get(`${API}/discover/moods`),
          axios.get(`${API}/discover/curated`)
        ]);
        setMoods(moodsRes.data.moods || []);
        setCurated(curatedRes.data.collections || []);
      } catch (e) {
        console.error("Error fetching discover data:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    if (user) fetchPlaylists();
  }, [user]);

  const fetchPlaylists = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/playlists`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setPlaylists(res.data.playlists || []);
    } catch (e) {}
  };

  const handleCreatePlaylist = async (e) => {
    e.preventDefault();
    if (!newPlaylistName.trim()) return;
    setCreating(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(`${API}/playlists`, {
        name: newPlaylistName,
        description: newPlaylistDesc,
        is_public: false
      }, { headers: { Authorization: `Bearer ${token}` }, withCredentials: true });
      setPlaylists(prev => [res.data, ...prev]);
      setNewPlaylistName("");
      setNewPlaylistDesc("");
      setShowCreatePlaylist(false);
      toast.success("Playlist created!");
    } catch (e) {
      toast.error("Failed to create playlist");
    } finally {
      setCreating(false);
    }
  };

  const handleDeletePlaylist = async (playlistId) => {
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/playlists/${playlistId}`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setPlaylists(prev => prev.filter(p => p.playlist_id !== playlistId));
      toast.success("Playlist deleted");
    } catch (e) {
      toast.error("Failed to delete playlist");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-neon-cyan animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen py-10 px-6" data-testid="discover-page">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-12">
          <Badge className="bg-neon-magenta/20 text-neon-magenta border-neon-magenta/50 mb-4">
            Discover
          </Badge>
          <h1 className="font-heading text-4xl md:text-6xl uppercase tracking-tight mb-4">
            Find Your Vibe
          </h1>
          <p className="text-zinc-400 max-w-2xl">
            Browse beats by mood, explore curated collections, or build your own playlists.
          </p>
        </div>

        {/* Recommended For You */}
        <section className="mb-16" data-testid="discover-recommendations">
          <RecommendedBeats limit={6} compact />
        </section>

        {/* Mood Cards Grid */}
        <section className="mb-16" data-testid="mood-section">
          <h2 className="font-heading text-xl uppercase tracking-wide mb-6 text-zinc-300">Moods</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {moods.map((mood, idx) => {
              const Icon = MOOD_ICONS[mood.name] || Sparkles;
              return (
                <motion.div
                  key={mood.name}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: idx * 0.05 }}
                >
                  <Link
                    to={`/discover/mood/${mood.name}`}
                    className="block group relative overflow-hidden h-36 p-5 transition-all hover:scale-[1.02]"
                    style={{ background: `linear-gradient(135deg, ${mood.color}33, ${mood.color}11)`, border: `1px solid ${mood.color}44` }}
                    data-testid={`mood-card-${mood.name.toLowerCase()}`}
                  >
                    <Icon className="w-8 h-8 mb-3 transition-transform group-hover:scale-110" style={{ color: mood.color }} />
                    <h3 className="font-heading text-lg uppercase tracking-wide" style={{ color: mood.color }}>
                      {mood.name}
                    </h3>
                    <p className="text-xs text-zinc-500 mt-1">{mood.beat_count} beats</p>
                    <div className="absolute bottom-0 right-0 w-24 h-24 rounded-full blur-3xl opacity-20" style={{ background: mood.color }} />
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </section>

        {/* Curated Collections */}
        <section className="mb-16" data-testid="curated-section">
          <h2 className="font-heading text-xl uppercase tracking-wide mb-6 text-zinc-300">Curated Collections</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {curated.map((coll, idx) => (
              <motion.div
                key={coll.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.08 }}
              >
                <Link
                  to={`/discover/collection/${coll.id}`}
                  className={`block group relative overflow-hidden h-48 p-6 bg-gradient-to-br ${coll.gradient} border border-white/10 hover:border-white/20 transition-all`}
                  data-testid={`collection-card-${coll.id}`}
                >
                  <h3 className="font-heading text-2xl uppercase tracking-wide mb-2">{coll.title}</h3>
                  <p className="text-sm text-zinc-400 mb-4">{coll.description}</p>
                  <div className="flex items-center gap-2">
                    {coll.moods.map(m => (
                      <Badge key={m} variant="outline" className="text-xs border-white/20">{m}</Badge>
                    ))}
                    <span className="text-xs text-zinc-500 ml-auto">{coll.beat_count} beats</span>
                  </div>
                  {/* Preview thumbnails */}
                  <div className="absolute bottom-3 right-3 flex -space-x-2">
                    {coll.preview_beats?.slice(0, 3).map((b, i) => (
                      <div key={i} className="w-8 h-8 bg-zinc-800 border border-white/10 overflow-hidden">
                        <img src={b.image_url || "https://images.unsplash.com/photo-1614149162883-504ce4d13909?w=50"} alt="" className="w-full h-full object-cover" />
                      </div>
                    ))}
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </section>

        {/* User Playlists */}
        <section data-testid="playlists-section">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-heading text-xl uppercase tracking-wide text-zinc-300">
              <ListMusic className="w-5 h-5 inline mr-2" />Your Playlists
            </h2>
            {user && (
              <Dialog open={showCreatePlaylist} onOpenChange={setShowCreatePlaylist}>
                <DialogTrigger asChild>
                  <Button size="sm" className="bg-neon-cyan text-black font-bold" data-testid="create-playlist-btn">
                    <Plus className="w-4 h-4 mr-1" /> New Playlist
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-zinc-900 border-white/10">
                  <DialogHeader><DialogTitle className="font-heading">Create Playlist</DialogTitle></DialogHeader>
                  <form onSubmit={handleCreatePlaylist} className="space-y-4">
                    <Input
                      value={newPlaylistName}
                      onChange={(e) => setNewPlaylistName(e.target.value)}
                      placeholder="Playlist name"
                      className="bg-black/50 border-white/10"
                      required
                      data-testid="playlist-name-input"
                    />
                    <Input
                      value={newPlaylistDesc}
                      onChange={(e) => setNewPlaylistDesc(e.target.value)}
                      placeholder="Description (optional)"
                      className="bg-black/50 border-white/10"
                      data-testid="playlist-desc-input"
                    />
                    <Button type="submit" disabled={creating} className="w-full bg-neon-cyan text-black font-bold" data-testid="save-playlist-btn">
                      {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                      Create Playlist
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            )}
          </div>

          {!user ? (
            <div className="glass p-8 text-center">
              <ListMusic className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
              <p className="text-zinc-400 mb-4">Sign in to create and manage your playlists</p>
            </div>
          ) : playlists.length === 0 ? (
            <div className="glass p-8 text-center">
              <ListMusic className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
              <p className="text-zinc-400 mb-2">No playlists yet</p>
              <p className="text-xs text-zinc-600">Create your first playlist to start organizing your favorite beats</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {playlists.map((pl) => (
                <div key={pl.playlist_id} className="glass p-5 group relative" data-testid={`playlist-${pl.playlist_id}`}>
                  <Link to={`/discover/playlist/${pl.playlist_id}`}>
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 bg-neon-cyan/10 flex items-center justify-center">
                        <ListMusic className="w-5 h-5 text-neon-cyan" />
                      </div>
                      <div>
                        <h3 className="font-medium">{pl.name}</h3>
                        <p className="text-xs text-zinc-500">{pl.beats?.length || 0} beats</p>
                      </div>
                    </div>
                    {pl.description && <p className="text-xs text-zinc-600 truncate">{pl.description}</p>}
                  </Link>
                  <div className="absolute top-3 right-3 flex items-center gap-1">
                    <ShareButton type="playlist" id={pl.playlist_id} title={pl.name} description={`${pl.beats?.length || 0} beats`} />
                    {pl.is_public ? <Globe className="w-3 h-3 text-zinc-500" /> : <Lock className="w-3 h-3 text-zinc-500" />}
                    <button
                      onClick={(e) => { e.preventDefault(); handleDeletePlaylist(pl.playlist_id); }}
                      className="opacity-0 group-hover:opacity-100 p-1 text-zinc-500 hover:text-red-400 transition-all"
                      data-testid={`delete-playlist-${pl.playlist_id}`}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default DiscoverPage;
